import { ReactNode } from 'react';
import { Navigate, Routes, Route, useSearchParams } from 'react-router-dom';
import { Protegido, useAuth } from './lib/auth';
import Layout from './components/Layout';
import Landing from './pages/Landing';
import Login from './pages/Login';
import Cadastro from './pages/Cadastro';
import Privacidade from './pages/Privacidade';
import Termos from './pages/Termos';
import Solucoes from './pages/Solucoes';
import Dashboard from './pages/Dashboard';
import DashboardOficina from './pages/DashboardOficina';
import Unidades from './pages/Unidades';
import Cadastros from './pages/Cadastros';
import OSLista from './pages/OSLista';
import OSNova from './pages/OSNova';
import OSNovaSimplificada from './pages/OSNovaSimplificada';
import ClienteUnificado from './pages/ClienteUnificado';
import OSDetalhe from './pages/OSDetalhe';
import Agenda from './pages/Agenda';
import Relatorios from './pages/Relatorios';
import Empresas from './pages/Empresas';
import Ativos from './pages/Ativos';
import Checklist from './pages/Checklist';
import Estoque from './pages/Estoque';
import Servicos from './pages/Servicos';
import Consumo from './pages/Consumo';
import RegistroConsumoLista from './pages/RegistroConsumoLista';
import PMOC from './pages/PMOC';
import ConfigEmpresa from './pages/ConfigEmpresa';
import Usuarios from './pages/Usuarios';
import QRAlvo from './pages/QRAlvo';
import AtivoPublico from './pages/AtivoPublico';
import MobileApp from './pages/MobileApp';
import Veiculos from './pages/Veiculos';
import Orcamentos from './pages/Orcamentos';
import ClientesOficina from './pages/ClientesOficina';
import MateriaisOficina from './pages/MateriaisOficina';
import RastreamentoTecnicos from './pages/RastreamentoTecnicos';
import Financeiro from './pages/Financeiro';
import Integracao from './pages/Integracao';
import FeedbackAlerts from './components/FeedbackAlerts';
import CookieConsent from './components/CookieConsent';

function AppIndex() {
  const { user } = useAuth();
  if (user?.perfil === 'admin_geral' && !user.empresa_id) return <Navigate to="/app/empresas" replace />;
  if (user?.modo_visualizacao === 'oficina') return <DashboardOficina />;
  return <Dashboard />;
}

function PremiumOnly({ children }: { children: ReactNode }) {
  const { isPremium } = useAuth();
  if (!isPremium) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function EmpresaAdminOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.perfil === 'admin_geral' && user.empresa_id) return <>{children}</>;
  if (!['admin_empresa', 'supervisor'].includes(user?.perfil || '')) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function UsuarioAdminOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (!user?.perfil || user.perfil === 'tecnico') return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function PremiumEmpresaAdminOnly({ children }: { children: ReactNode }) {
  return <PremiumOnly><EmpresaAdminOnly>{children}</EmpresaAdminOnly></PremiumOnly>;
}

// Estoque: igual a PremiumEmpresaAdminOnly, mas com uma excecao - permissao de
// usuario "TECNICO PODE CADASTRAR MATERIAL" deixa o tecnico entrar tambem
// (a regra tem que bater com o menu em Layout.tsx, senao um diverge do outro).
function EstoqueOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const tecnicoPodeCadastrarMaterial = user?.perfil === 'tecnico' && Boolean(user?.pode_cadastrar_material);
  if (tecnicoPodeCadastrarMaterial) return <PremiumOnly>{children}</PremiumOnly>;
  return <PremiumEmpresaAdminOnly>{children}</PremiumEmpresaAdminOnly>;
}

// "RETIRAR TELA PMOC": barra a rota. A API do PMOC tambem barra
// (exigirPmocDisponivel), entao nao ha caminho por URL nem por chamada direta.
function PmocOnly({ children }: { children: ReactNode }) {
  const { isSemPmoc } = useAuth();
  if (isSemPmoc) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function ConsumoOnly({ children }: { children: ReactNode }) {
  const { isConsumoDisponivel } = useAuth();
  if (!isConsumoDisponivel) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

// Gates das permissões de empresa "ATIVAR MODO FINANCEIRO" / "ATIVAR INTEGRAÇÃO PAGAMENTOS".
// A regra mora em auth.tsx para o menu e a rota nunca divergirem.
function FinanceiroOnly({ children }: { children: ReactNode }) {
  const { isFinanceiroDisponivel } = useAuth();
  if (!isFinanceiroDisponivel) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function IntegracaoPagamentosOnly({ children }: { children: ReactNode }) {
  const { isIntegracaoPagamentosDisponivel } = useAuth();
  if (!isIntegracaoPagamentosDisponivel) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

// Gate da permissão de empresa "SERVIÇOS" (menu lateral Serviços).
function ServicosOnly({ children }: { children: ReactNode }) {
  const { isServicosDisponivel } = useAuth();
  if (!isServicosDisponivel) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function FacilitiesOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.modo_visualizacao === 'oficina') return <Navigate to="/app/config" replace />;
  return <>{children}</>;
}

function ConfigRoute() {
  const { user } = useAuth();
  if (user?.modo_visualizacao === 'oficina') return <ConfigEmpresa />;
  return <EmpresaAdminOnly><ConfigEmpresa /></EmpresaAdminOnly>;
}

function OficinaOnly({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  if (user?.modo_visualizacao !== 'oficina') return <Navigate to="/app" replace />;
  return <>{children}</>;
}

function RelatoriosRoute() {
  const { user } = useAuth();
  if (user?.modo_visualizacao === 'oficina') return <OficinaOnly><EmpresaAdminOnly><Relatorios /></EmpresaAdminOnly></OficinaOnly>;
  return <FacilitiesOnly><EmpresaAdminOnly><Relatorios /></EmpresaAdminOnly></FacilitiesOnly>;
}

function OSDetalheRoute() {
  const { user } = useAuth();
  if (user?.modo_visualizacao === 'oficina') return <OficinaOnly><OSDetalhe /></OficinaOnly>;
  return <FacilitiesOnly><OSDetalhe /></FacilitiesOnly>;
}

function OSListaRoute() {
  const { user } = useAuth();
  if (user?.modo_visualizacao === 'oficina') return <OficinaOnly><OSLista /></OficinaOnly>;
  return <FacilitiesOnly><OSLista /></FacilitiesOnly>;
}

// Parametros que so o formulario COMPLETO sabe usar. Chegam prontos da leitura de
// QR Code (equipamento/local) e do Registro de Consumo, que abrem a OS ja
// apontando para um equipamento, um local ou uma leitura. A tela simplificada nao
// tem esses passos, entao abri-la nesses casos jogaria o contexto no lixo em
// silencio - por isso o formulario completo continua atendendo esses links.
const PARAMS_OS_COMPLETA = ['ativo', 'local', 'piso', 'unidade', 'consumo_leitura_id', 'consumo_alerta_id', 'descricao', 'tipo_os'];

// Criacao de OS: com a permissao de empresa "CRIACAO DE OS SIMPLIFICADA" ligada,
// o caminho curto (Cliente -> Servicos -> Tecnico -> Revisao -> Imprimir) e o
// padrao. O formulario completo de 8 passos NAO sai do ar: ?completa=1 continua
// abrindo ele, e a propria tela simplificada linka para la.
// Gate das telas que a permissao "CRIACAO DE OS SIMPLIFICADA" liga por completo
// (cadastro unificado do cliente e, adiante, o wizard de PMOC). Os endpoints por
// tras exigem gestor/supervisor, entao a rota exige o mesmo - senao a tela abriria
// para tecnico e todo botao responderia 403.
function OsSimplificadaOnly({ children }: { children: ReactNode }) {
  const { isOsSimplificadaDisponivel } = useAuth();
  if (!isOsSimplificadaDisponivel) return <Navigate to="/app" replace />;
  return <FacilitiesOnly><EmpresaAdminOnly>{children}</EmpresaAdminOnly></FacilitiesOnly>;
}

function OSNovaRoute() {
  const { user, isOsSimplificadaDisponivel } = useAuth();
  const [params] = useSearchParams();
  const temContextoDeEquipamento = PARAMS_OS_COMPLETA.some((p) => params.get(p));
  // Permissao "TECNICO NAO PODE CRIAR OS": o backend (os/os_criar.php) ja
  // recusa a requisicao, mas barramos a rota tambem para nao abrir um
  // formulario que vai falhar ao salvar.
  if (user?.perfil === 'tecnico' && user?.tecnico_nao_pode_criar_os) {
    return <Navigate to="/app/os" replace />;
  }
  if (isOsSimplificadaDisponivel && params.get('completa') !== '1' && !temContextoDeEquipamento) {
    return <OSNovaSimplificada />;
  }
  return <OSNova />;
}

function AgendaRoute() {
  const { user } = useAuth();
  if (user?.modo_visualizacao === 'oficina') return <OficinaOnly><Agenda /></OficinaOnly>;
  return <FacilitiesOnly><Agenda /></FacilitiesOnly>;
}

function RastreamentoRoute() {
  const { user } = useAuth();
  const podeRastrear = Boolean(
    user?.rastreamento_tecnico_habilitado
    && !(user?.perfil === 'tecnico' && user?.tecnico_sem_acesso_rastreamento_habilitado)
    && (user?.perfil === 'admin_geral' || user?.pode_ver_localizacao_tecnico),
  );
  if (!podeRastrear) return <Navigate to="/app" replace />;
  return <FacilitiesOnly><RastreamentoTecnicos /></FacilitiesOnly>;
}

export default function App() {
  return (
    <>
    <FeedbackAlerts />
    <CookieConsent />
    <Routes>
      {/* públicas */}
      <Route path="/" element={<Landing />} />
      <Route path="/fluxo" element={<Landing initialSection="fluxo" />} />
      <Route path="/porque" element={<Landing initialSection="porque" />} />
      <Route path="/modulos" element={<Landing initialSection="modulos" />} />
      <Route path="/integracoes" element={<Landing initialSection="integracoes" />} />
      <Route path="/planos" element={<Landing initialSection="planos" />} />
      <Route path="/login" element={<Login />} />
      <Route path="/cadastro" element={<Cadastro />} />
      <Route path="/privacidade" element={<Privacidade />} />
      <Route path="/termos" element={<Termos />} />
      <Route path="/solucoes" element={<Solucoes />} />
      <Route path="/mobile" element={<MobileApp />} />
      <Route path="/equipamento-publico/:empresaId/:codigo" element={<AtivoPublico />} />

      {/* protegidas (dentro do layout com menu) */}
      <Route path="/app" element={<Protegido><Layout /></Protegido>}>
        <Route index element={<AppIndex />} />
        <Route path="empresas" element={<FacilitiesOnly><Empresas /></FacilitiesOnly>} />
        <Route path="cadastros" element={<FacilitiesOnly><EmpresaAdminOnly><Cadastros /></EmpresaAdminOnly></FacilitiesOnly>} />
        <Route path="unidades" element={<FacilitiesOnly><EmpresaAdminOnly><Unidades /></EmpresaAdminOnly></FacilitiesOnly>} />
        <Route path="usuarios" element={<UsuarioAdminOnly><Usuarios /></UsuarioAdminOnly>} />
        <Route path="config" element={<ConfigRoute />} />
        <Route path="veiculos" element={<OficinaOnly><Veiculos /></OficinaOnly>} />
        <Route path="clientes" element={<OficinaOnly><ClientesOficina /></OficinaOnly>} />
        <Route path="materiais" element={<OficinaOnly><MateriaisOficina /></OficinaOnly>} />
        <Route path="orcamentos" element={<OficinaOnly><Orcamentos /></OficinaOnly>} />
        <Route path="ativos" element={<FacilitiesOnly><PremiumEmpresaAdminOnly><Ativos /></PremiumEmpresaAdminOnly></FacilitiesOnly>} />
        <Route path="checklist" element={<FacilitiesOnly><PremiumEmpresaAdminOnly><Checklist /></PremiumEmpresaAdminOnly></FacilitiesOnly>} />
        <Route path="estoque" element={<FacilitiesOnly><EstoqueOnly><Estoque /></EstoqueOnly></FacilitiesOnly>} />
        <Route path="servicos" element={<ServicosOnly><Servicos /></ServicosOnly>} />
        {/* Wizard "Novo PMOC" puxando do cadastro do cliente. Rota ESTATICA antes
            da dinamica, e o modulo PMOC segue exigindo Premium no backend. */}
        <Route path="pmoc" element={<PmocOnly><FacilitiesOnly><PremiumEmpresaAdminOnly><PMOC /></PremiumEmpresaAdminOnly></FacilitiesOnly></PmocOnly>} />
        <Route path="pmoc/ativo/:ativoId" element={<PmocOnly><FacilitiesOnly><PremiumEmpresaAdminOnly><PMOC /></PremiumEmpresaAdminOnly></FacilitiesOnly></PmocOnly>} />
        <Route path="registro-consumo" element={<FacilitiesOnly><ConsumoOnly><RegistroConsumoLista /></ConsumoOnly></FacilitiesOnly>} />
        <Route path="registro-consumo/:ativoId" element={<FacilitiesOnly><ConsumoOnly><Consumo modo="registro" /></ConsumoOnly></FacilitiesOnly>} />
        <Route path="consumo" element={<FacilitiesOnly><ConsumoOnly><Consumo modo="medidores" /></ConsumoOnly></FacilitiesOnly>} />
        <Route path="consumo/:ativoId" element={<FacilitiesOnly><ConsumoOnly><Consumo modo="registro" /></ConsumoOnly></FacilitiesOnly>} />
        <Route path="os" element={<OSListaRoute />} />
        <Route path="os/nova" element={<FacilitiesOnly><OSNovaRoute /></FacilitiesOnly>} />
        {/* Cadastro unificado do cliente (Nome -> Endereco -> Documentos ->
            Equipamentos -> Ambientes). So existe com a permissao ligada. */}
        <Route path="cliente" element={<OsSimplificadaOnly><ClienteUnificado /></OsSimplificadaOnly>} />
        <Route path="cliente/:id" element={<OsSimplificadaOnly><ClienteUnificado /></OsSimplificadaOnly>} />
        <Route path="os/:id" element={<OSDetalheRoute />} />
        <Route path="agenda" element={<AgendaRoute />} />
        <Route path="rastreamento" element={<RastreamentoRoute />} />
        <Route path="relatorios" element={<RelatoriosRoute />} />
        <Route path="financeiro" element={<FinanceiroOnly><Financeiro /></FinanceiroOnly>} />
        <Route path="integracao" element={<IntegracaoPagamentosOnly><Integracao /></IntegracaoPagamentosOnly>} />
        {/* Leitura de QR (equipamento ATV- ou local LOC-). O backend gateia premium por tipo.
            Rota com empresaId embute o tenant no QR; a antiga (só :codigo) segue no tenant logado. */}
        <Route path="qr/:empresaId/:codigo" element={<FacilitiesOnly><QRAlvo /></FacilitiesOnly>} />
        <Route path="qr/:codigo" element={<FacilitiesOnly><QRAlvo /></FacilitiesOnly>} />
      </Route>

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}
